import { createAuditLogEntry } from '../../graphql/mutations';
import { logger } from '../../utils/logger';
import { fetchGraphQLRequest } from './clients/graphqlClient';

/**
 * Maximum length for error_log field in dim_audit_log table.
 * Matches the constraint used by audit_helper.py in the backend.
 */
const MAX_ERROR_LOG_LENGTH = 1000;

/**
 * Default operation type for audit log entries.
 * Most VTL mutations are standard CRUD operations.
 */
const DEFAULT_OPERATION_TYPE = 'crud';

/**
 * Serialize an error of any shape into a string for the error_log field.
 * Uses JSON.stringify to capture the complete error structure for debugging.
 * Falls back to String() for non-serializable values.
 */
const serializeError = (error: unknown): string => {
  if (typeof error === 'string') return error;
  try {
    return JSON.stringify(error);
  } catch {
    return String(error);
  }
};

export interface AuditConfig {
  /** Identifier for the page/operation, matching stored procedure audit entries */
  pageName: string;
  /** Type of operation — defaults to 'crud'. Use 'bulk_edit' for bulk operations. */
  operationType?: string;
}

/**
 * Fire-and-forget audit logger for VTL mutation errors.
 *
 * Called automatically by fetchGraphQLRequest when auditConfig is provided
 * and the mutation fails. Sends a separate createAuditLogEntry mutation
 * that runs in its own RDS Data API transaction, ensuring the error audit
 * log persists even though the original mutation's transaction was rolled back.
 *
 * This function NEVER throws — it silently catches any errors to avoid
 * interfering with the main error flow back to the user.
 *
 * @param auditConfig - The audit configuration with pageName and optional operationType
 * @param error - The original error from the failed mutation
 * @param payload - The original mutation variables (serialized as additional_info)
 * @param userAlias - The user who triggered the mutation (from cached auth session)
 */
export const logVtlMutationError = async (
  auditConfig: AuditConfig,
  error: unknown,
  payload: unknown,
  userAlias: string
): Promise<void> => {
  try {
    const errorLog = serializeError(error).slice(0, MAX_ERROR_LOG_LENGTH);
    const additionalInfo = JSON.stringify(payload ?? {}, (_key, value) =>
      value instanceof Date ? value.toISOString() : value
    );

    await fetchGraphQLRequest({
      query: createAuditLogEntry,
      variables: {
        input: {
          page_name: auditConfig.pageName,
          operation_type: auditConfig.operationType ?? DEFAULT_OPERATION_TYPE,
          error_log: errorLog,
          additional_info: additionalInfo,
          created_by: userAlias,
        },
      },
      requestName: 'createAuditLogEntry',
      requestType: 'mutation',
    });
  } catch (auditError) {
    // Never throw — audit logging failure should not affect the main error flow
    logger.error('auditLogger: Failed to persist error audit log entry', {
      auditError,
      pageName: auditConfig.pageName,
    });
  }
};
